import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Terminal, AlertCircle, CheckCircle, Trash2, Copy, Plus, RefreshCw, ShieldCheck, Eye, EyeOff, X } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import { toast } from '../Toast';
import './ServiceApiKeys.css';

const SCOPE_OPTIONS = [
  { id: 'read', label: 'Read Only', description: 'Can read data but not modify' },
  { id: 'read-write', label: 'Read & Write', description: 'Can read and modify data' },
];

function ServiceApiKeys() {
  const queryClient = useQueryClient();
  const [keyName, setKeyName] = useState('');
  const [selectedScope, setSelectedScope] = useState('read');
  const [newKeyValue, setNewKeyValue] = useState(null);
  const [showNewKey, setShowNewKey] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedPrefix, setCopiedPrefix] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [revokeConfirm, setRevokeConfirm] = useState(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['service-api-keys'],
    queryFn: () => apiClient.get('/api/api-keys'),
  });

  const createMutation = useMutation({
    mutationFn: ({ name, scopes }) =>
      apiClient.post('/api/api-keys', { name, scopes }),
    onSuccess: (result) => {
      setNewKeyValue(result.key);
      setKeyName('');
      setSelectedScope('read');
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['service-api-keys'] });
    },
    onError: (err) => {
      setSaveError(err.message || 'Failed to create key');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (keyId) =>
      apiClient.del(`/api/api-keys/${keyId}`),
    onSuccess: () => {
      setRevokeConfirm(null);
      toast('Key revoked');
      queryClient.invalidateQueries({ queryKey: ['service-api-keys'] });
    },
    onError: (err) => {
      // A failed revoke used to do nothing visible — the key looked revoked
      // until the next reload (slice 4).
      setRevokeConfirm(null);
      toast(`Couldn’t revoke the key — ${err.message || 'try again'}`, 'error');
    },
  });

  const handleCreate = (e) => {
    e.preventDefault();
    if (!keyName.trim()) return;
    setSaveError(null);
    setNewKeyValue(null);
    // Map read-write to ['read', 'classify'] for backend
    const scopes = selectedScope === 'read-write' ? ['read', 'classify'] : ['read'];
    createMutation.mutate({ name: keyName.trim(), scopes });
  };

  const copyToClipboard = async (text, isPrefix = false, prefixId = null) => {
    try {
      await navigator.clipboard.writeText(text);
      if (isPrefix) {
        setCopiedPrefix(prefixId);
        setTimeout(() => setCopiedPrefix(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      if (isPrefix) {
        setCopiedPrefix(prefixId);
        setTimeout(() => setCopiedPrefix(null), 2000);
      } else {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  };

  const handleRevoke = (keyId) => {
    revokeMutation.mutate(keyId);
  };

  const dismissNewKey = () => {
    setNewKeyValue(null);
    setCopied(false);
    setShowNewKey(true);
  };

  const formatKeyPrefix = (key) => {
    if (key && key.length > 16) {
      return `${key.substring(0, 16)}...`;
    }
    return key || '—';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatLastUsed = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  const formatScopes = (scopes) => {
    if (!scopes || scopes.length === 0) return 'read';
    if (scopes.includes('classify') || scopes.includes('write')) return 'read-write';
    return 'read';
  };

  const keys = (data?.keys || data || []).filter(k => !k.revoked_at);

  return (
    <div className="service-api-keys">
      <header className="sak-header">
        <div className="sak-title">
          <Terminal size={24} />
          <div>
            <h1>Service API Keys</h1>
            <p>
              Generate API keys for external integrations like Google Apps Script.
              Keys are hashed before storage — you will only see the full key once at creation.
            </p>
          </div>
        </div>
      </header>

      <section className="sak-callout">
        <ShieldCheck size={20} />
        <div>
          <strong>Security</strong>
          <ul>
            <li>Keys use the <code>bdx_ts_</code> prefix and are 64 hex characters.</li>
            <li>Keys are SHA-256 hashed before storage — we never store plaintext.</li>
            <li>Pass your key in the <code>X-API-Key</code> header to authenticate.</li>
            <li>Revoked keys immediately return 401 Unauthorized.</li>
          </ul>
        </div>
      </section>

      {/* One-time key display modal */}
      {newKeyValue && (
        <div className="sak-modal-backdrop">
          <div className="sak-modal">
            <div className="sak-modal-header">
              <CheckCircle size={20} />
              <h3>API Key Created</h3>
              <button className="sak-modal-close" onClick={dismissNewKey} title="Close">
                <X size={18} />
              </button>
            </div>
            <div className="sak-modal-warning">
              Copy this key now — you won't be able to see it again.
            </div>
            <div className="sak-modal-key">
              <code>{showNewKey ? newKeyValue : '•'.repeat(40)}</code>
              <div className="sak-modal-actions">
                <button
                  className="sak-btn-icon"
                  onClick={() => setShowNewKey(v => !v)}
                  title={showNewKey ? 'Hide' : 'Show'}
                >
                  {showNewKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button className="sak-btn-copy" onClick={() => copyToClipboard(newKeyValue)}>
                  <Copy size={14} />
                  {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            </div>
            <button className="sak-btn-dismiss" onClick={dismissNewKey}>
              I've saved my key
            </button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className="sak-status">
          <RefreshCw size={16} className="sak-spin" />
          <span>Loading your keys…</span>
        </div>
      )}

      {error && (
        <div className="sak-status sak-error">
          <AlertCircle size={16} />
          <span>{error.message}</span>
        </div>
      )}

      {!isLoading && !error && (
        <section className="sak-card">
          <h2>Your Keys</h2>
          {keys.length === 0 ? (
            <div className="sak-empty">
              No service API keys yet. Create one below to enable external integrations.
            </div>
          ) : (
            <div className="sak-list">
              {keys.map(k => (
                <div key={k.id} className="sak-item">
                  <div className="sak-item-main">
                    <div className="sak-item-name">
                      <CheckCircle size={16} />
                      <strong>{k.name}</strong>
                      <span className={`sak-scope-badge ${formatScopes(k.scopes)}`}>
                        {formatScopes(k.scopes)}
                      </span>
                    </div>
                    <div className="sak-item-meta">
                      <span className="sak-prefix-row">
                        Prefix: <code>{formatKeyPrefix(k.key_prefix)}</code>
                        <button
                          className="sak-btn-copy-small"
                          onClick={() => copyToClipboard(k.key_prefix, true, k.id)}
                          title="Copy prefix"
                        >
                          <Copy size={12} />
                          {copiedPrefix === k.id ? 'Copied' : ''}
                        </button>
                      </span>
                      <span>Created {formatDate(k.created_at)}</span>
                      <span>Last used: {formatLastUsed(k.last_used_at)}</span>
                    </div>
                  </div>
                  {revokeConfirm === k.id ? (
                    <div className="sak-revoke-confirm">
                      <span>Are you sure?</span>
                      <button
                        className="sak-btn-revoke-yes"
                        onClick={() => handleRevoke(k.id)}
                        disabled={revokeMutation.isPending}
                      >
                        {revokeMutation.isPending ? 'Revoking…' : 'Yes, revoke'}
                      </button>
                      <button
                        className="sak-btn-cancel"
                        onClick={() => setRevokeConfirm(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      className="sak-btn-revoke"
                      onClick={() => setRevokeConfirm(k.id)}
                      disabled={revokeMutation.isPending}
                    >
                      <Trash2 size={14} /> Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      <section className="sak-card">
        <h2>Create New Key</h2>
        <form className="sak-form" onSubmit={handleCreate}>
          <div className="sak-field">
            <label>Key Name</label>
            <input
              type="text"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="e.g., Google Apps Script, CI/CD Pipeline"
              disabled={createMutation.isPending}
              autoComplete="off"
              spellCheck={false}
            />
            <small>A descriptive name to identify where this key is used.</small>
          </div>

          <div className="sak-field">
            <label>Permissions</label>
            <div className="sak-scope-options">
              {SCOPE_OPTIONS.map(opt => (
                <label key={opt.id} className={`sak-scope-option ${selectedScope === opt.id ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="scope"
                    value={opt.id}
                    checked={selectedScope === opt.id}
                    onChange={(e) => setSelectedScope(e.target.value)}
                    disabled={createMutation.isPending}
                  />
                  <div className="sak-scope-content">
                    <strong>{opt.label}</strong>
                    <span>{opt.description}</span>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {saveError && (
            <div className="sak-status sak-error">
              <AlertCircle size={16} />
              <span>{saveError}</span>
            </div>
          )}

          <div className="sak-form-actions">
            <button
              type="submit"
              className="sak-btn-create"
              disabled={createMutation.isPending || !keyName.trim()}
            >
              {createMutation.isPending ? (
                <><RefreshCw size={14} className="sak-spin" /> Creating…</>
              ) : (
                <><Plus size={14} /> Create Key</>
              )}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default ServiceApiKeys;
